import { Form, Formik } from "formik";
import * as Yup from "yup";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { type RadioItemProps } from "@vega-core/components/Form/Controls/Radio/RadioGroupField/RadioGroupField";
import { RadioGroupField } from "@vega-core/components/Form/Fields/RadioGroupField";
import { HtmlContent } from "@vega-core/components/HtmlContent/HtmlContent";
import { m } from "@vega-core/paraglide/messages";

import { type ConfirmationContentBox } from "./content";

type Props = {
  heading?: string;
  description?: string;
  confirmLabel?: string;
  declineLabel?: string;
  boxes?: ConfirmationContentBox[];
  isSubmitting: boolean;
  onSubmit: (hasAccepted: boolean) => void;
};

export const ConfirmationStepView = ({
  heading,
  description,
  confirmLabel,
  declineLabel,
  boxes,
  isSubmitting,
  onSubmit,
}: Props) => {
  const options: RadioItemProps[] = [
    {
      title: confirmLabel ?? m.welcome_accreditation_confirm(),
      value: "true",
    },
    {
      title: declineLabel ?? m.welcome_accreditation_reject(),
      value: "false",
    },
  ];

  const validationSchema = Yup.object({
    accreditation: Yup.string().required(m.formValidation_requiredText()),
  });

  return (
    <div className="max-w-screen-xl p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 [grid-template-areas:'intro'_'criteria'_'form'] lg:[grid-template-areas:'intro_criteria'_'form_criteria']">
      <div className="content-end lg:col-span-1 [grid-area:intro] lg:[grid-area:intro_form]">
        <div className="space-y-4 text-left">
          {heading ? <h4 className="heading-4">{heading}</h4> : null}
          {description ? (
            <div className="body-base-regular text-text/primary">
              <HtmlContent content={description} />
            </div>
          ) : null}
        </div>
      </div>
      <div className="lg:col-span-1 [grid-area:form] lg:[grid-area:intro_form]">
        <Formik
          initialValues={{ accreditation: "" }}
          onSubmit={(values) => {
            onSubmit(values.accreditation === "true");
          }}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, isValid, values }) => (
            <Form className="flex flex-col space-y-4" onSubmit={handleSubmit}>
              <div className="text-left">
                <RadioGroupField
                  items={options}
                  labelVariant="compact"
                  name="accreditation"
                  variant="vertical"
                />
              </div>
              <ThemedButton
                className="w-48 place-self-center lg:place-self-auto"
                dataTestId="continue-button"
                disabled={
                  !isValid || isSubmitting || values.accreditation === ""
                }
                fullWidth
                isLoading={isSubmitting}
                type="submit"
                variant="primary"
              >
                {m.btn_continue()}
              </ThemedButton>
            </Form>
          )}
        </Formik>
      </div>
      {boxes && boxes.length > 0 ? (
        <div className="lg:col-start-2 [grid-area:criteria] lg:[grid-area:criteria] row-span-2 flex flex-col gap-4">
          {boxes.map((box) => (
            <div
              className="body-base-regular bg-background/primary text-left divide-y divide-border/primary rounded-lg border border-border/primary"
              key={box.id}
            >
              <h2 className="text-text/primary body-large-semibold m-3">
                {box.heading}
              </h2>
              <div className="p-4">
                <HtmlContent content={box.body} />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
